import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar, IconButton, Toolbar, Box, Divider, List, ListItem, ListItemButton,
  ListItemText, Drawer, Grid, Container, Avatar, Tooltip, Menu, MenuItem,
  Typography,
  Badge,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import brokenImage from 'assets/images/broken-image.png';

import { selectAuthUser } from 'base/auth/selectors';
import useAuthentication from 'base/auth/hooks/use-authentication';
import { authSliceActions } from 'base/auth/slice';
import { selectEnterprise, selectNotifications } from 'base/core/selectors';

import './styles.scss';
import { getAvatarLetters, getUserHubUrl } from 'utils/common-utils';
import { ReferenceType } from 'pages/sessions/components/session-summary/interface';
import FeedbackForm from '../feedback-form';

const drawerStyles = {
  flexShrink: 0,
  display: { xs: 'block', md: 'none' },
  '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 180 },
};
// const getStartBtnStyles = { ml: { md: '20px', lg: '35px', xl: '48px' } };
const desktopNavStyles = { display: { xs: 'none', md: 'block' }, width: '100%' };
const mobileNavStyles = { display: { xs: 'flex', md: 'none', sm: 'flex' }, width: '55px', ml: '2' };
const toolbarStylesDesktop = { minHeight: { md: '80px !important' } };
const toolbarStylesMobile = { minHeight: { xs: '50px !important', sm: '50px !important' } };
const menuItemStyles = { color: 'text.primary', fontSize: '1rem', fontWeight: 600 };
const appBarStylesDesktop = { display: { xs: 'none', md: 'block' }, backgroundColor: 'background.paper', borderBottom: '1px solid #E7E7F1' };
const appBarStylesMobile = { display: { xs: 'block', md: 'none' }, backgroundColor: 'background.paper', borderBottom: '1px solid #E7E7F1' };
const menuBtnStylesMobile = {
  mr: 0, ml: 'auto', display: { xs: 'block', md: 'none' }, color: 'black',
};
const menuBtnStylesDesktop = { mr: 2, display: { xs: 'box', md: 'none' }, color: 'black' };
const userMenuProps = {
  elevation: 0,
  sx: {
    overflow: 'visible',
    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: 1.5,
    '& .MuiAvatar-root': {
      width: 32,
      height: 32,
      ml: -0.5,
      mr: 1,
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 14,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
    },
  },
};

const navItems = [
  { label: 'Overview', route: '/overview' },
  { label: 'Schedule', route: '/schedules' },
  { label: 'Sessions', route: '/sessions' },
  { label: 'Groups', route: '/groups' },
  { label: 'Users', route: '/users' },
  { label: 'Newsfeed', route: '/newsfeed' },
];

const settings = [
  'Profile',
  'User hub',
  'My media',
  'Forms',
  'Articles',
  'Reviews',
  'Feedback',
  'Sign out',
];

const TopBar = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthentication();
  const isLogged = isAuthenticated();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const signOut = useCallback(() => dispatch(authSliceActions.signOut()), [dispatch]);
  const authUser = useSelector(selectAuthUser);
  const enterpiseData = useSelector(selectEnterprise);
  const userHubUrl = getUserHubUrl();
  const notifications = useSelector(selectNotifications);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const isElement = (element: EventTarget | null): element is Element => element instanceof Element;

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (isElement(event.target) && !event.target.closest('.MuiDrawer-paper') && mobileOpen) {
      setMobileOpen(false);
    }
  }, [mobileOpen]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleCloseUserMenu = (menu: string) => {
    setAnchorElUser(null);
    if (menu === 'Profile') {
      navigate('/profile/');
    } else if (menu === 'User hub') {
      Object.assign(document.createElement('a'), {
        target: '_blank',
        rel: 'noopener noreferrer',
        href: userHubUrl,
      }).click();
    } else if (menu === 'My media') {
      navigate('/media-library/root');
    } else if (menu === 'Articles') {
      navigate('/articles');
    } else if (menu === 'Forms') {
      navigate('/forms');
    } else if (menu === 'Wallet') {
      navigate('/wallet');
    } else if (menu === 'Reviews') {
      navigate('/reviews');
    } else if (menu === 'Feedback') {
      setOpen(!open);
    } else if (menu === 'Sign out') {
      signOut();
    }
  };
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <div className="my-1">
        <img src={enterpiseData.logo} alt={enterpiseData.name} height="35" />
      </div>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.route} disablePadding>
            <ListItemButton href={item.route} sx={{ textAlign: 'center' }}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}

        <ListItem>
          <ListItemButton href="/sessions/messages" sx={{ textAlign: 'center' }}>
            <Badge badgeContent={(notifications.unreadMessages + notifications.unreadDirectMessages) || undefined} color="secondary">
              <ListItemText
                primary="Messages"
                sx={{
                  paddingLeft: (notifications.unreadMessages + notifications.unreadDirectMessages) ? '20px' : '0px',
                }}
              />
            </Badge>
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const guestMenuItems = (
    <>
      <Grid item md={6} />
      <Grid item md={3} sx={{ textAlign: 'right' }}>
        {/* <IconButton size="large" onClick={handleLoginClick}>
          <PersonIcon color="primary" />
        </IconButton> */}
      </Grid>
    </>
  );

  const expertUserMenuItems = (
    <>
      <Grid item sx={{ textAlign: 'center' }}>
        {navItems.map((item) => (
          <Link key={item.label} to={item.route} className="topbar-link-items">
            <Typography align="center" sx={menuItemStyles}>{item.label}</Typography>
          </Link>
        ))}

        <Badge badgeContent={(notifications.unreadMessages + notifications.unreadDirectMessages) || undefined} color="secondary" sx={{ '> a': { pr: 0 } }}>
          <Link to="/sessions/messages" className="topbar-link-items">
            <Typography align="center" sx={menuItemStyles}>Messages</Typography>
          </Link>
        </Badge>
      </Grid>
      <Grid item md={1} lg={2} sx={{ textAlign: 'right' }}>
        <Tooltip title="Open settings">
          <IconButton aria-label="handle-user-menu-button" onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            <Avatar alt={getAvatarLetters(authUser)} src={authUser?.profileImgUrl}>
              {getAvatarLetters(authUser)}
            </Avatar>
            <KeyboardArrowDownIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Menu
          id="menu-appbar"
          anchorEl={anchorElUser}
          PaperProps={userMenuProps}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          open={Boolean(anchorElUser)}
          onClose={() => handleCloseUserMenu('')}
        >
          {settings.map((setting) => (
            <MenuItem aria-label={`setting-item-${setting}`} key={setting} onClick={() => handleCloseUserMenu(setting)}>
              <Typography textAlign="center">{setting}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Grid>
    </>
  );

  const expertUserMenuItemsForMobile = (
    <Grid item md={2} lg={2} sx={{ textAlign: 'right', m: 0 }}>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, m: 0 }}>
          <Avatar alt={getAvatarLetters(authUser)} src={authUser?.profileImgUrl}>
            {getAvatarLetters(authUser)}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorElUser}
        PaperProps={userMenuProps}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(anchorElUser)}
        onClose={() => handleCloseUserMenu('')}
      >
        {settings.map((setting) => (
          <MenuItem key={setting} onClick={() => handleCloseUserMenu(setting)}>
            <Typography textAlign="center">{setting}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Grid>
  );

  return (
    <>
      <FeedbackForm open={open} reference="" referenceType={ReferenceType.GENERAL} />

      <AppBar component="nav" position="sticky" sx={appBarStylesDesktop}>
        <Container fixed>
          <Toolbar disableGutters sx={toolbarStylesDesktop}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={menuBtnStylesDesktop}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={desktopNavStyles}>
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item md={1} lg={2} sx={{ textAlign: 'left' }}>
                  <a href={userHubUrl} rel="noreferrer">
                    {enterpiseData.logo !== ''
                      ? (
                        <Box
                          component="img"
                          alt={enterpiseData.name}
                          src={enterpiseData.logo}
                          height={{ xs: 45, md: 55, display: 'block' }}
                          width="auto"
                        />
                      ) : (
                        <Box
                          component="img"
                          src={brokenImage}
                          height={{ xs: 45, md: 55, display: 'block' }}
                          width="auto"
                        />
                      ) }

                  </a>
                </Grid>
                {isLogged && expertUserMenuItems}
                {!isLogged && guestMenuItems}
              </Grid>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <AppBar component="nav" position="sticky" sx={appBarStylesMobile}>
        <Container fixed>
          <Toolbar disableGutters sx={toolbarStylesMobile}>
            <a href={userHubUrl} rel="noreferrer">
              <Box
                component="img"
                alt={enterpiseData.name}
                src={enterpiseData.logo}
                height={{ xs: 35, md: 55, display: 'block' }}
                width="auto"
              />
            </a>
            {isLogged && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={menuBtnStylesMobile}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={mobileNavStyles}>
              <Grid container direction="row-reverse" justifyContent="space-between" alignItems="center">
                {isLogged && expertUserMenuItemsForMobile}
                {!isLogged && guestMenuItems}
              </Grid>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="nav">
        <Drawer
          variant="persistent"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={drawerStyles}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};
export default TopBar;
